import {
  Box,
  Divider as MUIDivider,
  IconButton,
  styled
} from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { Customer } from 'Services/Mongo/Types';
import { PriceButton } from 'pages/products/components/price-button';
import { ColoredPill } from 'pages/products/components/colored-pill';
import { useDialog } from '@setvi/shared/providers';
import { PromotionDialog } from 'pages/products/components/promotion';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import {
  PriceGroup,
  Pricing,
  Product,
  ProductDetails,
  ProductPriceType,
  ProductStock
} from '@setvi/shared/interfaces';
import CloseIcon from '@material-ui/icons/Close';
import { getProductStatus } from 'componentsV2/product/status';
import { ThreeSixtyView } from 'componentsV2/product/product-details/components';
import { ProductLabel, ProductStatusBadge } from 'componentsV2';
import { getProductsPrices } from 'pages/products/api';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { distances } from '@setvi/shared/styles';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { FullPrice, ProductValue } from './components';
import { ProductHeaderStock } from './components/stock';

const Divider = styled(MUIDivider)({
  background: '#AFBAC6',
  margin: `0px ${distances.px.base}`
});

interface ProductShowcaseProps {
  product: Product;
  selectedCustomer: Customer;
  hideProductPrice?: boolean;
  hideManufacturer?: boolean;
  pricing: Pricing[];
  selectedPriceGroup?: PriceGroup;
  priceLoading?: boolean;
  priceType?: ProductPriceType;
  productDetails: ProductDetails;
  stockRefresh: ProductStock['refresh'];
  updateProduct?: (product: Product, props: Partial<Product>) => void;
  captureResourceAnalytics?: () => void;
  onStockClick?: () => void;
  closeDetailsDialog?: () => void;
}

export const Header = ({
  product,
  pricing,
  selectedCustomer,
  hideProductPrice = false,
  hideManufacturer = false,
  priceType,
  stockRefresh,
  priceLoading,
  selectedPriceGroup,
  productDetails,
  updateProduct,
  captureResourceAnalytics,
  onStockClick,
  closeDetailsDialog
}: ProductShowcaseProps) => {
  const { openDialog, closeDialog } = useDialog();
  const { mutationFn } = getProductsPrices();
  const mutation = useMutation(mutationFn);
  const { enqueueSnackbar } = useSnackbar();
  // @ts-ignore
  const leadTime = product?.custom?.ship_lead_time__business_days?.value;

  const handleGetPrice = async () => {
    try {
      const response = await mutation.mutateAsync({
        body: {
          accountNumber: selectedCustomer?.accountNumber,
          products: [{ sku: product.sku, quantity: 1 }]
        }
      });
      const { sell, cost } = response?.pricing?.[0];
      const newValues = {
        price: sell,
        ...(Number(cost) ? { cost } : {})
      };
      updateProduct?.(product, newValues);
    } catch (error) {
      enqueueSnackbar('Error occured while fetching product price', {
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  };

  const handlePromotionsPillClick = () => {
    openDialog(
      <PromotionDialog
        product={product}
        onClose={() => closeDialog('promotion-dialog')}
      />,
      {
        id: 'promotion-dialog'
      }
    );
  };

  const status = getProductStatus?.(product) || '';

  return (
    <Box display="flex" gridGap="12px" flex={1} justifyContent="space-between">
      <Box>
        <Box display="flex">
          {product?.promotions?.length > 0 && (
            <ColoredPill
              color="#FF572233"
              title="Promotions"
              imageUrl="/images/product-promotion.png"
              onClick={handlePromotionsPillClick}
            />
          )}
        </Box>
        <SText
          maxLines={2}
          weight="bold"
          lineHeight="28px"
          size="xl"
          title={toTitleCase(product?.name || '')}
        />
        <Box display="flex" alignItems="center" mt={2}>
          <ProductValue title="SKU:" value={product?.sku || ''} />
          {!hideManufacturer && product?.vendor?.name && (
            <Box display="flex" alignItems="center" gridGap="4px">
              <ProductValue
                bullet
                title="MFG:"
                value={`${toTitleCase(product?.vendor?.name || '-')} ${product?.manufacturerSku ? `(${product?.manufacturerSku})` : ''}`}
              />
              {product?.privateLabel && <ProductLabel />}
              {product?.vendorHierarchy > 0 && (
                <ProductLabel type="strategic-vendor" />
              )}
            </Box>
          )}

          {!hideProductPrice && (
            <Box display="flex" alignItems="center">
              {priceType === ProductPriceType.List && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <SText
                    style={{ marginRight: 6 }}
                    title={product?.price?.toLocaleString('en-US', {
                      currency: 'USD',
                      style: 'currency'
                    })}
                  />
                </>
              )}
              {priceType === ProductPriceType.Get && !!selectedCustomer && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <PriceButton
                    price={product?.price}
                    loading={priceLoading}
                    onGetPrice={handleGetPrice}
                    style={{
                      borderRadius: 120,
                      fontSize: 12,
                      minHeight: 'max-content'
                    }}
                  />
                </>
              )}
              {priceType === ProductPriceType.Full && product && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <FullPrice
                    pricing={pricing}
                    product={product}
                    onGetPrice={handleGetPrice}
                    filterActive={!!selectedCustomer || !!selectedPriceGroup}
                  />
                </>
              )}
              {priceType === ProductPriceType.GetFull && !!selectedCustomer && (
                <>
                  <Divider orientation="vertical" flexItem />
                  {product.price ? (
                    <FullPrice
                      pricing={pricing}
                      product={product}
                      onGetPrice={handleGetPrice}
                      filterActive={!!selectedCustomer}
                    />
                  ) : (
                    <PriceButton
                      price={product?.price}
                      loading={priceLoading}
                      onGetPrice={handleGetPrice}
                      style={{
                        borderRadius: 120,
                        fontSize: 12,
                        minHeight: 'max-content'
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          )}

          {(productDetails?.stock.show || !!leadTime) && (
            <Divider orientation="vertical" flexItem />
          )}
          {productDetails?.stock.show && (
            <ProductHeaderStock
              product={product}
              selectedCustomer={selectedCustomer}
              stockRefresh={stockRefresh}
              onClick={onStockClick}
              updateProduct={updateProduct}
            />
          )}
          {leadTime && (
            <ProductValue
              bullet={productDetails?.stock.show}
              title="Lead time:"
              value={leadTime}
            />
          )}
          {(!!status || !!product?.stockInfo) && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box display="flex" alignItems="center" gridGap={6}>
                {status && (
                  <ProductStatusBadge
                    style={{
                      minWidth: 'fit-content'
                    }}>
                    <SText size="xs" title={getProductStatus(product)} />
                  </ProductStatusBadge>
                )}
                {!!product?.stockInfo && (
                  <ProductStatusBadge>
                    <SText size="xs" title={getStockInfo(product?.stockInfo)} />
                  </ProductStatusBadge>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box display="flex" gridGap="6px" alignItems="flex-start">
        {/* This is only for MiddleBY */}
        <ThreeSixtyView
          productSku={product?.sku}
          captureResourceAnalytics={captureResourceAnalytics}
        />

        {closeDetailsDialog && (
          <IconButton
            aria-label="close"
            size="small"
            style={{ height: 'fit-content' }}
            onClick={() => closeDetailsDialog()}>
            <CloseIcon style={{ width: 27, height: 27 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
